import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAcademyStore } from '../../store/academyStore';
import { COURSES } from '../../data/curriculum';
import { BadgeCheck, CheckCircle2, ArrowLeft, Download } from 'lucide-react';
import { BackButton } from '../ui/BackButton';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const COURSE_THEMES: Record<string, { primary: string, light: string, text: string }> = {
    'digital-foundations': { primary: '#0891b2', light: '#ecfeff', text: '#155e75' },
    'foundation': { primary: '#16a34a', light: '#f0fdf4', text: '#166534' },
    'production': { primary: '#2563eb', light: '#eff6ff', text: '#1e40af' },
    'data-detective': { primary: '#d97706', light: '#fffbeb', text: '#92400e' },
    'ai-operator': { primary: '#9333ea', light: '#faf5ff', text: '#6b21a8' },
    'cyber-security': { primary: '#dc2626', light: '#fef2f2', text: '#991b1b' },
    'automation': { primary: '#ea580c', light: '#fff7ed', text: '#9a3412' },
    'ui-ux': { primary: '#db2777', light: '#fdf2f8', text: '#9d174d' },
    'sovereign-systems': { primary: '#0d9488', light: '#f0fdfa', text: '#115e59' },
    'game-dev': { primary: '#e11d48', light: '#fff1f2', text: '#9f1239' },
    'logic-architect': { primary: '#4f46e5', light: '#eef2ff', text: '#3730a3' },
};

const COURSE_SUMMARIES: Record<string, string> = {
    'digital-foundations': "Has successfully demonstrated a foundational understanding of digital citizenship, exhibiting proficiency in online safety, privacy, identifying phishing threats, and evaluating the credibility of digital information. The recipient has established a strong analytical mindset through preliminary visual coding challenges, laying the groundwork for safe and responsible exploration of the digital landscape.",
    'foundation': "Has successfully mastered the core principles of the JavaScript programming language and fundamental computer science architecture. The recipient has demonstrated practical expertise in variables, control flow logic, data structures, and the implementation of dynamic systems through hands-on algorithms and practical API generation, proving their capability as a capable digital builder.",
    'production': "Has proven extensive competency in backend engineering, cloud architecture, and database management. The recipient has successfully designed relational data models, implemented robust CRUD operations, secured real-time data subscriptions, and managed database migrations, thereby proving capable of architecting scalable and production-ready applications.",
    'data-detective': "Has successfully demonstrated mastery over structured data analytics and relational database querying. The recipient has exhibited high-level proficiency in SQL, data sifting, aggregation math, complex table joins, and dynamic dashboard creation, proving an outstanding ability to extract actionable insights from raw data pipelines.",
    'ai-operator': "Has demonstrated advanced proficiency in applied artificial intelligence and prompt engineering. The recipient has successfully engineered contextual prompts, managed model parameters, integrated Retrieval-Augmented Generation (RAG) concepts, and orchestrated multi-agent collaborations, proving capable of directing autonomous AI systems to solve complex problems.",
    'cyber-security': "Has proven expert-level comprehension of essential cybersecurity principles, defensive architectures, and vulnerability mitigation. The recipient has effectively analyzed the CIA triad, defended against targeted network intrusions, mitigated SQL injection and XSS vulnerabilities, and successfully implemented robust authentication systems to fortify digital structures against adversarial threats.",
    'automation': "Has successfully gained an advanced understanding of systems automation and event-driven architecture. The recipient has demonstrated the ability to construct self-sustaining digital workflows by configuring cron schedulers, mastering webhook catchers, and navigating complex OAuth data exchanges, effectively creating efficient, hands-free machine operations.",
    'ui-ux': "Has exhibited exceptional capability in frontend design, user experience architecture, and responsive layouts. The recipient has mastered the CSS box model, advanced typography, color theory, and modern layout algorithms including Flexbox and CSS Grid, proving their ability to build accessible, visually striking, and deeply intuitive user interfaces.",
    'sovereign-systems': "Has successfully mastered infrastructure sovereignty, server configuration, and continuous deployment networking. The recipient has demonstrated practical skills in deploying Docker containers, managing reverse proxies through Nginx, monitoring real-time server metrics, and integrating production-grade payment architectures, proving capable of commanding an independent digital ecosystem.",
    'game-dev': "Has successfully demonstrated advanced logic programming through the rigorous development of interactive software and physics engines. The recipient has engineered custom game loops, implemented hit-box collision detection, constructed scoring state machines, and fine-tuned visual game polish, proving exceptional capability in software design and interactive media.",
    'logic-architect': "Has proven comprehensive mastery of the React.js framework and state-driven interface development. The recipient has successfully engineered isolated modular components, governed dynamic uni-directional data flow using functional props and hooks, and synchronized application state intelligently with external API effects, proving advanced capability in modern frontend engineering."
};

export const CertificateView = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAcademyStore();
    const [scale, setScale] = useState(1);
    const [isGenerating, setIsGenerating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const course = COURSES.find(c => c.id === courseId);

    const theme = (courseId && COURSE_THEMES[courseId]) || { primary: '#475569', light: '#f8fafc', text: '#334155' };

    // Print and Scale Logic
    useEffect(() => {
        const handleResize = () => {
            if (!containerRef.current) return;
            const padding = 40; // Space around
            const availableWidth = window.innerWidth - padding;
            const availableHeight = window.innerHeight - padding;

            const contentWidth = 1100; // Fixed aspect ratio width
            const contentHeight = 900; // Increased to fit footer properly

            const scaleW = availableWidth / contentWidth;
            const scaleH = availableHeight / contentHeight;

            // Use the smaller scale to fit both dimensions, max 1 (don't upscale pixelated)
            // Actually for 'rendering to one image', upscaling might be fine if SVG/CSS. 
            // But let's cap at 1.2 for slight zoom on large screens, mostly shrink for mobile.
            const newScale = Math.min(Math.min(scaleW, scaleH), 1.2);
            setScale(newScale);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        // Removed auto-print dialog. Wait for user to click download.
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleDownloadPdf = async () => {
        if (!containerRef.current || !currentUser || !course) return;
        
        setIsGenerating(true);
        const currentScale = scale;
        // Temporarily reset scale so html2canvas computes pixel bounds correctly
        setScale(1);
        await new Promise(r => setTimeout(r, 150));

        try {
            // Wait a moment for fonts/images
            const canvas = await html2canvas(containerRef.current, {
                scale: 2, 
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [1100, 900]
            });
            
            pdf.addImage(imgData, 'JPEG', 0, 0, 1100, 900);
            pdf.save(`${currentUser.username}_${course.title.replace(/\s+/g, '_')}_Certificate.pdf`);
        } catch (err) {
            console.error('Failed to generate PDF', err);
            alert('Failed to generate PDF. Check console for details.');
        } finally {
            setScale(currentScale);
            setIsGenerating(false);
        }
    };

    if (!currentUser || !course) {
        return <div>Certificate not found.</div>;
    }

    return (
        <div className="min-h-screen bg-slate-800 flex items-center justify-center overflow-hidden print:overflow-visible print:bg-white print:block">
            {/* Nav Buttons */}
            <div className="fixed top-4 left-4 print:hidden z-50 flex gap-4">
                <button
                    onClick={() => navigate('/profile')}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-lg hover:bg-slate-50 text-slate-600 font-bold transition-transform hover:scale-105"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                <button
                    onClick={handleDownloadPdf}
                    disabled={isGenerating}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg shadow-lg hover:bg-green-400 font-bold transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                    {isGenerating ? <BadgeCheck className="animate-pulse" size={16} /> : <Download size={16} />}
                    {isGenerating ? 'Generating...' : 'Download PDF'}
                </button>
            </div>

            {/* Scaled Container */}
            <div
                className="transition-transform duration-200 ease-out origin-center print:transform-none print:w-full print:h-full print:m-0"
                style={{ transform: `scale(${scale})` }}
            >
                <div
                    ref={containerRef}
                    className="w-[1100px] h-[900px] bg-white text-center relative shadow-2xl print:shadow-none mx-auto overflow-hidden flex flex-col"
                    style={{
                        border: `24px solid ${theme.primary}`,
                        backgroundImage: `
                            radial-gradient(circle at center, white 50%, ${theme.light} 100%),
                            repeating-linear-gradient(45deg, ${theme.light} 0px, ${theme.light} 2px, transparent 2px, transparent 12px)
                        `
                    }}
                >
                    {/* Inner Border */}
                    <div className="absolute inset-2 border-4 border-double border-yellow-500 pointer-events-none opacity-50 mix-blend-multiply m-2 p-2 shadow-[inset_0_0_20px_rgba(0,0,0,0.05)]"></div>

                    {/* Corner Flourishes */}
                    {[0, 90, 180, 270].map((rot, i) => (
                        <div key={i} className="absolute w-16 h-16 pointer-events-none opacity-80"
                            style={{
                                top: i < 2 ? '1rem' : 'auto',
                                bottom: i >= 2 ? '1rem' : 'auto',
                                left: i === 0 || i === 3 ? '1rem' : 'auto',
                                right: i === 1 || i === 2 ? '1rem' : 'auto',
                                color: theme.primary,
                                transform: `rotate(${rot}deg)`
                            }}>
                                <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M0,0 L40,0 C60,0 60,20 80,20 M0,0 L0,40 C0,60 20,60 20,80" />
                                    <path d="M10,10 L40,10 C50,10 50,20 60,20 M10,10 L10,40 C10,50 20,50 20,60" opacity="0.5" />
                                </svg>
                        </div>
                    ))}

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col items-center py-10 px-24 z-10 relative h-full">

                        {/* Header */}
                        <div className="mt-0 shrink-0">
                            <div className="flex items-center justify-center gap-3 mb-1 opacity-70" style={{ color: theme.primary }}>
                                <BadgeCheck size={28} />
                                <span className="uppercase tracking-[0.3em] text-[10px] font-bold">Gorilla Scouts Academy</span>
                                <BadgeCheck size={28} />
                            </div>

                            <h1 className="text-5xl font-serif font-black mb-0 uppercase tracking-wide leading-none drop-shadow-sm" style={{ color: theme.text }}>
                                Certificate
                            </h1>
                            <h2 className="text-2xl font-serif italic text-slate-400 font-light">
                                of Proficiency
                            </h2>
                        </div>

                        {/* Main Text */}
                        <div className="flex flex-col items-center w-full my-2 shrink-0">
                            <p className="text-md text-slate-500 italic mb-1 font-serif">This document certifies that</p>

                            <div
                                className="text-4xl font-bold font-comic mb-1 px-12 py-1 border-b-2 inline-block min-w-[500px]"
                                style={{ color: theme.primary, borderColor: theme.light }}
                            >
                                {currentUser.username}
                            </div>

                            <p className="text-md text-slate-500 italic mt-1 mb-1 font-serif">
                                has officially mastered the curriculum for
                            </p>

                            <h3
                                className="text-2xl font-black uppercase tracking-widest px-8 py-2 rounded-xl border-2 bg-white/50 backdrop-blur-sm shadow-sm"
                                style={{ color: theme.text, borderColor: theme.light }}
                            >
                                {course.title}
                            </h3>
                        </div>

                        {/* Course Summary Paragraph */}
                        <div className="w-full max-w-4xl mt-6 shrink-0 text-center px-12">
                            <p className="text-xl leading-relaxed text-slate-600 font-serif italic selection:bg-none">
                                {COURSE_SUMMARIES[course.id] || "Has successfully demonstrated theoretical and practical mastery of the subject material, satisfying the highest standards of the academy's rigorous qualification process."}
                            </p>
                            
                            {/* Standards Alignment */}
                            {course.standards && (
                                <div className="mt-4 flex flex-col items-center justify-center gap-2 opacity-80">
                                    <div className="flex items-center gap-3">
                                        <img src="/assets/stem_badge.png" alt="STEM Certified" className="w-8 h-8 object-contain" />
                                        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-1">Certified Academic Outcomes</div>
                                        <img src="/assets/stem_badge.png" alt="STEM Certified" className="w-8 h-8 object-contain" />
                                    </div>
                                    <div className="flex flex-wrap justify-center gap-2 text-[8px] font-mono text-slate-500 max-w-3xl">
                                        {[
                                            { label: 'ISTE-S', value: course.standards.iste },
                                            { label: 'FL-CS', value: course.standards.fl.split(' ')[0] },
                                            { label: 'CSTA', value: course.standards.csta },
                                            { label: 'CCSS-M', value: course.standards.ccss },
                                            { label: 'NGSS', value: course.standards.ngss },
                                            { label: 'STEL', value: course.standards.stel },
                                        ]
                                        .filter(s => s.value)
                                        .map((s, i) => (
                                            <div key={i} className="bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100 flex items-center gap-1.5 shadow-sm">
                                                <strong className="text-slate-700">{s.label}:</strong> 
                                                <span>{s.value?.split(' (')[0]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer / Auth */}
                        <div className="w-full flex justify-between items-end mt-auto relative pb-2 pt-16">
                            {/* Date */}
                            <div className="text-center w-56 relative border-t-2 border-slate-300 pt-2">
                                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-48 h-12 flex items-end justify-center pointer-events-none opacity-0">
                                    {/* Placeholder matches signature height */}
                                </div>
                                <div className="w-full font-bold text-slate-600 font-mono text-sm">
                                    {new Date().toLocaleDateString()}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Date Certified</div>
                            </div>

                            {/* Centered Seal */}
                            <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-2">
                                <div className="w-28 h-28 flex items-center justify-center transform -rotate-6 transition-transform hover:rotate-0">
                                    <img
                                        src="/assets/official_seal.png"
                                        alt="Official Seal"
                                        className="w-full h-full object-contain mix-blend-multiply opacity-90 drop-shadow-xl"
                                    />
                                </div>
                            </div>

                            {/* Signature */}
                            <div className="text-center w-56 relative border-t-2 border-slate-300 pt-2">
                                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-48 h-20 flex items-end justify-center pointer-events-none">
                                    <img
                                        src="/assets/signature_bambam.png"
                                        alt="Bam Bam Signature"
                                        className="h-full w-full object-contain mix-blend-multiply opacity-90 filter contrast-125"
                                    />
                                </div>
                                <div className="w-full font-bold font-comic text-slate-800 relative z-10 text-sm">
                                    Bam Bam
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Scout Leader</div>
                            </div>
                        </div>
                    </div>

                    <style type="text/css" media="print">{`
                        @page { size: landscape; margin: 0; }
                        body { background: white; -webkit-print-color-adjust: exact; padding: 0; margin: 0; }
                    `}</style>
                </div>
            </div>
        </div>
    );
};
